import styled from '@emotion/styled';
import isPropValid from '@emotion/is-prop-valid';
import React, { useContext } from 'react';
import { darken } from 'polished';
import { StaticQuery, graphql } from 'gatsby';
import { InternalLink } from './link';
import { routes, pageTypes } from '../util/routes';
import breakpoints from '../util/breakpoints';
import { Z_TOP, Z_ABOVE_MIDDLE } from '../util/zIndex';
import MenuButton from '../components/menu-button';
import { PageContext } from '../context/page-context';

export const headerHeight = 45;

const Header = styled.header({
  position: `relative`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: `center`,
  flexShrink: 0
});

export default Header;

export const HeaderNav = styled(Header)(props => ({
  display: 'flex',
  boxShadow: '0px 1px 2px rgba(0,0,0,.5)',
  zIndex: Z_TOP,
  justifyContent: `flex-start`,
  fontSize: `14px`,
  border: `1px solid red`,
  [breakpoints.sm]: {
    fontSize: `14px`
  }
}));

const HeaderLogo = styled.img(props => ({
  display: `flex`,
  width: '150px',
  height: `32px`,
  marginBottom: `0px`
}));

const NavItem = styled(InternalLink, { shouldForwardProp: prop =>
    isPropValid(prop)
})( props => ({
  margin: props.absoluteCenter ? `0px auto` : `inherit`,
  padding: `20px 20px`,
  cursor: `pointer`,
  display: `flex`,
  alignItems: `center`,
  height: `100%`,
  justifyContent: `center`,
  color: props.active ? props.theme.primaryAccent || props.theme.primary : `#ccc`,
  borderBottom: props.active
    ? `2px solid ${props.theme.primaryAccent || props.theme.primary}`
    : `2px solid transparent`,

  [breakpoints.lg]: {
    padding: `0px 8px`
  },
  [breakpoints.md]: {
    display: props.hideMobile ? 'none' : `flex`,
    borderBottom:
      props.active && !props.disableHighlightMobile
        ? `2px solid ${props.theme.primaryAccent || props.theme.primary}`
        : `2px solid transparent`
  },
  [breakpoints.sm]: {
    fontSize: `16px`,
    padding: `0px 2px`
  },
  [`:hover`]: {
    color: !props.active && (props.theme.primaryAccent || props.theme.primary)
  }
}));

const NavItems = styled.div(props => ({
  flex: 1,
  display: `flex`,
  width: `100%`,
  height: `45px`,
  justifyContent: `space-between`,
  alignItems: `stretch`,
  [breakpoints.md]: {
    justifyContent: props.spaceBetweenMobile ? `space-between` : `space-evenly`,
    display: props.hideMobile ? `none` : `flex`
  }
}));

const NavigationBar = styled.nav(props => ({
  width: `100%`,
  backgroundColor: `#111`,
  borderBottom: `1px solid ${darken(0.3, props.theme.primaryAccent || props.theme.primary)}`,
  flexDirection: `row`,
  justifyContent: `space-between`,
  zIndex: Z_TOP,
  top: `0px`,
  flexWrap: `wrap`,
  position: `fixed`,
  minHeight: `${headerHeight}px`,
  alignItems: `stretch`
}));

const NavigationBarBottom = styled(NavigationBar)(props => ({
  bottom: `0px`,
  top: `auto`,
  display: props.hideDesktop ? `none` : `flex`,
  zIndex: Z_ABOVE_MIDDLE,
  [breakpoints.md]: {
    borderTop: `1px solid ${darken(0.3, props.theme.primaryAccent || props.theme.primary)}`,
    top: `inherit`,
    display: props.hideMobile ? `none` : `flex`
  }
}));

const HeaderTitle = styled.div(props => ({
  width: `1fr`,
  display: `flex`,
  justifyContent: `center`,
  alignItems: `center`,
  lineHeight: `100%`,
  position: `relative`,
  fontSize: `18px`,
  [breakpoints.sm]: {
    fontSize: `15px`,
    width: `100%`
  }
}));

const GameIcon = styled.img(props => ({
  width: `28px`,
  height: `28px`,
  marginRight: `10px`
}));

const NotificationBell = styled.div({
  display: `flex`,
  alignItems: `center`,
  height: `100%`,
  width: `30px`,
  marginRight: `10px`
});

const IconLink = styled(InternalLink)(({ uri }) => ({
  display: `none`,
  width: `50px`,
  height: `31px`,
  backgroundSize: `contain`,
  backgroundPosition: `center`,
  backgroundRepeat: `no-repeat`,
  [breakpoints.md]: {
    display: `flex`
  },
  backgroundImage: `url(${uri})`
}));

export const StyledMobileHeader = ({
  onMenuClick,
  gameName,
  hideBottomNav,
  EntityConfigs,
  pathName,
  gameIconURI
}) => {
  let items = [];

  const { logEvent } = useContext(PageContext);

  if (gameName) {
    items.push({
      name: 'Tier Lists',
      pageType: pageTypes.TIER_LIST_HUB,
      active: routes.TierListHub(gameName) === pathName,
      route: routes.TierListHub(gameName)
    });
  }

  if (EntityConfigs) {
    EntityConfigs.forEach(entityType => {
      items.push({
        pageType: pageTypes.ENTITY_HUB,
        name: entityType.plural,
        route: routes.EntityHub(gameName, entityType.plural)
      });
    });
  }

  if (gameName) {
    items = items.concat([
      {
        name: 'Guides',
        pageType: pageTypes.POST_HUB,
        route: routes.PostHub(gameName)
      },
      {
        name: 'Patch Notes',
        pageType: pageTypes.PATCH_HUB,
        route: routes.PatchHub(gameName)
      }
    ]);
  }

  const gameLevelNavElements = hideMobile => {
    return (
      items &&
      items.length !== 0 && (
        <>
          {items.map(item => {
            return (
              <NavItem
                key={item.route+item.name}
                active={pathName.includes(item.route)}
                to={item.route}
                hideMobile={hideMobile}
                onClick={() => {
                  logEvent('navbar_click', {
                    destinationPath: item.route,
                    destinationPageType: item.pageType
                  });
                }}
              >
                {item.name}
              </NavItem>
            );
          })}
        </>
      )
    );
  };

  const gameLevelNavElementsTop = gameLevelNavElements(true);
  const gameLevelNavElementsBottom = gameLevelNavElements(false);

  return (
    <StaticQuery
      query={graphql`
        {
          site {
            siteMetadata {
              logoURI
              logoIconURI
            }
          }
        }
      `}
      render={data => {
        const { logoURI, logoIconURI } = data.site.siteMetadata;
        return (
          <>
            <NavigationBar>
              <NavItems spaceBetweenMobile>
                <div
                  style={{
                    padding: `20px 0px`,
                    cursor: `pointer`,
                    display: `flex`,
                    alignItems: `center`,
                    height: `100%`
                  }}
                >
                  <MenuButton onClick={onMenuClick} />

                  {gameName && (
                    <NavItem to="/" title={`GamerHub Home`} hideMobile >
                      <HeaderLogo src={logoURI} alt={`GamerHub Logo`} />
                    </NavItem>
                  )}
                </div>
                {!gameName && (
                  <NavItem to="/" title={`GamerHub Home`} >
                    <HeaderLogo src={logoURI} alt={`GamerHub Logo`} />
                  </NavItem>
                )}
                {gameName && (
                  <NavItem
                    disableHighlightMobile
                    to={routes.GameHub(gameName)}
                    active={pathName.endsWith(routes.GameHub(gameName))}
                    onClick={() => {
                      logEvent(`navbar_click`, {
                        destinationPageType: pageTypes.GAME_HUB,
                        destinationPath: routes.GameHub(gameName)
                      });
                    }}
                  >
                    {gameIconURI && <GameIcon src={gameIconURI} alt={`${gameName} icon`} hideMobile />}
                    <HeaderTitle>
                      <span>{`${gameName} Wiki`}</span>
                    </HeaderTitle>
                  </NavItem>
                )}

                {gameLevelNavElementsTop}
                <NotificationBell>
                  {gameName && <IconLink to="/" uri={logoIconURI} />}
                </NotificationBell>
              </NavItems>
            </NavigationBar>

            <NavigationBarBottom hideDesktop hideMobile={hideBottomNav || !gameName}>
              <NavItems>
                {gameName && (
                  <NavItem to="/" title={`GamerHub Home`} hideMobile>
                    <HeaderLogo src={logoURI} alt={`GamerHub Logo`} />
                  </NavItem>
                )}
                {gameLevelNavElementsBottom}
                {gameName && (
                  <NavItem to={routes.GameHub(gameName)} hideMobile>
                    {gameIconURI && <GameIcon src={gameIconURI} alt={`${gameName} icon`} />}
                    <span>{`${gameName} Wiki`}</span>
                  </NavItem>
                )}
                {!gameName && (
                  <NavItem to="/" absoluteCenter hideMobile title={`GamerHub Home`} >
                    <HeaderLogo src={logoURI} alt={`GamerHub Logo`} center />
                  </NavItem>
                )}
              </NavItems>
            </NavigationBarBottom>
          </>
        );
      }}
    />
  );
};
